/**
 * SelectionManager - Centralized Piece Selection Logic
 *
 * Phase 4 - SOC Refactoring
 *
 * Consolidates all piece selection validation and rules from UI layer (Square.tsx)
 * into the engine layer. Removes ~150 lines of game logic from the UI.
 *
 * RESPONSIBILITIES:
 * - Validate which pieces can be selected
 * - Validate which pieces can be deselected
 * - Determine if selection should be locked
 * - Handle bonus turn piece validation
 * - Virus auto-execution logic
 * - Cheat detection (bonus turn violations)
 */

import { GameState, IPiece, IMove, Position, Mode, State, PieceType, MoveType } from '../types';
import { GameStateManager } from './GameStateManager';

export interface SelectionValidation {
  allowed: boolean;
  reason?: string;
  autoExecute?: boolean; // For virus auto-execution
}

export class SelectionManager {
  private gameStateManager: GameStateManager;

  constructor(gameStateManager: GameStateManager) {
    this.gameStateManager = gameStateManager;
  }

  /**
   * Validate if a piece can be selected
   * Consolidates logic from Square.tsx handlePieceClick
   */
  canSelectPiece(
    piece: IPiece | undefined,
    position: Position,
    options: {
      isLightPlayer: boolean;
      isThisPlayersTurn: boolean;
      selectionLocked: boolean;
      currentSelectedPiece?: { piece: IPiece; position: Position };
    }
  ): SelectionValidation {
    const gameState = this.gameStateManager.getGameState();

    // DEBUG: Log all inputs for observe mode investigation

    // Rule 1: Rules mode blocks all interactions
    if (gameState.mode === Mode.Rules) {
      return { allowed: false, reason: 'Rules mode - interactions disabled' };
    }

    // Rule 2: Setup phases block interactions
    if (gameState.state === State.PieceSelection || gameState.state === State.KingSelection) {
      return { allowed: false, reason: 'Setup phase - piece selection disabled' };
    }

    // Rule 3: No piece at position - allow for deselection
    if (!piece) {
      return { allowed: true, reason: 'Empty square - will deselect' };
    }

    // Rule 4: Selection locked (bonus turn in progress)
    if (options.selectionLocked && options.currentSelectedPiece) {
      const currentPiece = options.currentSelectedPiece.piece;

      // Allow clicking the same piece
      if (piece.id === currentPiece.id) {
        // Special case: Virus during bonus turn - check for auto-execution
        if (
          currentPiece.type === PieceType.Virus &&
          gameState.activeBonusTurnPieceId === currentPiece.id
        ) {
          return {
            allowed: true,
            reason: 'Virus bonus turn - same piece clicked',
            autoExecute: true // Signal to check for endTurn move
          };
        }

        return { allowed: true, reason: 'Same piece clicked during lock' };
      }

      // Block selecting different piece when locked
      if (piece.id !== currentPiece.id) {
        // Virus cheat detection logging
        if (currentPiece.type === PieceType.Virus) {
        }
        return { allowed: false, reason: 'Selection locked - cannot change piece' };
      }
    }

    // Rule 5: Ownership validation for piece selection
    // Local modes bypass ownership check (player controls both sides)
    const localModes = [Mode.Dev, Mode.Sandbox, Mode.TestAI, Mode.Tutorial];
    if (localModes.includes(gameState.mode)) {
      return { allowed: true, reason: 'Local mode - all pieces selectable' };
    }

    // NEW RULE: Allow selecting opponent pieces for OBSERVE MODE
    // In Live mode, players should be able to click opponent pieces to see their moves
    // (moves will be shown greyed out and not executable)
    const isOwnPiece = piece.isLight === options.isLightPlayer;
    if (gameState.mode === Mode.Live && !isOwnPiece) {
      return { allowed: true, reason: 'Opponent piece - observe mode' };
    }

    // Also allow opponent piece selection in AI mode for observe
    if (gameState.mode === Mode.AI && !isOwnPiece) {
      return { allowed: true, reason: 'Opponent piece - observe mode (AI)' };
    }

    // Turn validation - must be this player's turn to select OWN pieces
    if (!options.isThisPlayersTurn && isOwnPiece) {
      return { allowed: false, reason: 'Not your turn' };
    }

    // Allow selecting own pieces
    if (isOwnPiece) {
      return { allowed: true, reason: 'Own piece' };
    }

    // Allow selecting zombified enemy pieces (opponent controls them for one turn)
    if (piece.zombifiedStatus?.isZombified) {
      return { allowed: true, reason: 'Zombified enemy piece - opponent controls' };
    }

    // ZOMBIE INK: Allow selecting zombie ink shadows during owner's turn
    // The owner must find their real octopus among the shadows (guessing game)
    if (piece.isZombieInkShadow && piece.zombieInkTargetColor === options.isLightPlayer) {
      return { allowed: true, reason: 'Zombie ink shadow - owner must identify real octopus' };
    }

    // Block selecting enemy non-zombified pieces (fallback - should be caught by observe mode above)
    return { allowed: false, reason: 'Cannot select opponent piece' };
  }

  /**
   * Validate if current selection can be deselected
   */
  canDeselectPiece(selectionLocked: boolean): boolean {
    // Cannot deselect when selection is locked (bonus turn)
    return !selectionLocked;
  }

  /**
   * Determine if selection should be locked after a move
   * Used by bonus turn system (Monkey, Virus, Penguin)
   */
  shouldLockSelection(piece: IPiece, moveType: MoveType): boolean {
    // Monkey swing/consume moves lock selection for bonus turn
    if (piece.type === PieceType.Monkey && (moveType === MoveType.swing || moveType === MoveType.consume)) {
      return true;
    }

    // Virus moves lock selection for bonus turn
    if (piece.type === PieceType.Virus && moveType === MoveType.infect) {
      return true;
    }

    // Penguin capture moves lock selection for bonus turn
    if (piece.type === PieceType.Penguin && moveType === MoveType.capture) {
      return true;
    }

    return false;
  }

  /**
   * Check if a move can be executed during selection lock
   * Prevents cheat attempts where non-bonus pieces try to move
   */
  canExecuteMoveDuringLock(
    move: IMove,
    selectionLocked: boolean,
    currentGameState: GameState
  ): SelectionValidation {
    if (!selectionLocked) {
      return { allowed: true, reason: 'Not locked' };
    }

    // CRITICAL: Use fresh game state to handle Firebase sync piece ID changes
    const isActiveBonusTurnPiece =
      currentGameState.activeBonusTurnPieceId &&
      move.pieceId === currentGameState.activeBonusTurnPieceId;

    if (isActiveBonusTurnPiece) {
      return { allowed: true, reason: 'Active bonus turn piece' };
    }

    // Cheat detection: Non-bonus piece trying to move during lock

    return { allowed: false, reason: 'Selection locked - only bonus turn piece can move' };
  }

  /**
   * Check if virus should auto-execute its only available move
   * Virus auto-execution happens when:
   * 1. Selection is locked (bonus turn)
   * 2. There's only one valid move
   */
  shouldAutoExecuteVirus(
    piece: IPiece,
    validMoves: IMove[],
    selectionLocked: boolean
  ): boolean {
    if (piece.type !== PieceType.Virus) {
      return false;
    }

    // Auto-execute when locked and only one move available
    if (selectionLocked && validMoves.length === 1) {
      return true;
    }

    return false;
  }

  /**
   * Find endTurn move for virus (used when virus is clicked during bonus turn)
   */
  findVirusEndTurnMove(validMoves: IMove[]): IMove | undefined {
    return validMoves.find(m => m.moveType === MoveType.endTurn);
  }

  /**
   * Log penguin selection for debugging
   * (Temporary - can be removed once penguin issues are resolved)
   */
  logPenguinSelection(piece: IPiece, gameState: GameState): void {
    if (piece.type !== PieceType.Penguin) {
      return;
    }

    const isZombified = piece.zombifiedStatus?.isZombified || false;
    const hasBonusTurn = gameState.activeBonusTurnPieceId === piece.id;
    const moveStatus = isZombified && !hasBonusTurn
      ? 'ROTATIONS ONLY'
      : isZombified && hasBonusTurn
      ? 'SLIDE/CAPTURE ONLY'
      : 'ALL MOVES';

  }
}
